<template>
  <el-dialog
    v-model="dialogVisible"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    width="min(468px, calc(100vw - 32px))"
    align-center
    aria-label="登录与身份认证提示"
    class="login-auth-dialog"
    @close="handleClose"
  >
    <div class="dialog-content">
      <div class="dialog-text">
        <p>{{ descriptionText }}</p>
        <p v-if="!isLoggedIn || !isIdentityVerified">
          <template v-if="!isLoggedIn">
            请前往<button
              type="button"
              class="link-text"
              @click="handleGoLogin"
            >
              【登录】
            </button>。
          </template>
          <template v-else>
            请前往<button
              type="button"
              class="link-text"
              @click="handleGoAuth"
            >
              【身份认证】
            </button>完成实名认证。
          </template>
        </p>
      </div>
      <div class="dialog-footer">
        <el-button
          class="cancel-btn"
          @click="handleCancel"
        >
          {{ cancelButtonText }}
        </el-button>
        <el-button
          type="primary"
          class="confirm-btn"
          @click="handleConfirm"
        >
          {{ confirmButtonText }}
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus'

export default {
  name: 'LoginAuthDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    /** 弹窗场景：query 知产查询 | filing 数字备案 | monitoring 版权风险监测 | ipManagement 知识产权管理 | contractDownload 合同模版下载 | reportDownload 前沿报告下载 | attachmentDownload 附件下载 */
    scene: {
      type: String,
      default: 'query'
    }
  },
  emits: ['update:modelValue', 'confirm', 'cancel'],
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    isLoggedIn() {
      return this.$auth.isLoggedIn()
    },
    isIdentityVerified() {
      return this.$auth.isIdentityVerified()
    },
    descriptionText() {
      const messages = {
        query: {
          loggedOut: '请先登录并完成实名认证后方可查询并查看知识产权数据资产详情;',
          unverified: '请先完成实名认证后方可查询并查看知识产权数据资产详情;'
        },
        filing: {
          loggedOut: '请先登录并完成实名认证后方可进行数字备案;',
          unverified: '请先完成实名认证后方可进行数字备案;'
        },
        monitoring: {
          loggedOut: '请先登录并完成实名认证后方可创建版权风险监测任务;',
          unverified: '请先完成实名认证后方可创建版权风险监测任务;'
        },
        ipManagement: {
          loggedOut: '请先登录并完成实名认证后方可进入知识产权管理;',
          unverified: '请先完成实名认证后方可进入知识产权管理;'
        },
        contractDownload: {
          loggedOut: '请先登录并完成实名认证后方可下载合同模版;',
          unverified: '请先完成实名认证后方可下载合同模版;'
        },
        reportDownload: {
          loggedOut: '请先登录并完成实名认证后方可下载报告;',
          unverified: '请先完成实名认证后方可下载报告;'
        },
        attachmentDownload: {
          loggedOut: '请先登录并完成实名认证后方可下载附件;',
          unverified: '请先完成实名认证后方可下载附件;'
        }
      }
      const sceneMessages = messages[this.scene] || messages.query
      return !this.isLoggedIn ? sceneMessages.loggedOut : sceneMessages.unverified
    },
    cancelButtonText() {
      return this.isLoggedIn ? '暂不认证' : '暂不登录'
    },
    confirmButtonText() {
      return this.isLoggedIn ? '去认证' : '去登录'
    }
  },
  methods: {
    handleClose() {
      this.$emit('update:modelValue', false)
    },
    handleCancel() {
      this.$emit('cancel')
      this.handleClose()
    },
    handleConfirm() {
      this.$emit('confirm')
      if (this.isLoggedIn && !this.isIdentityVerified) {
        this.handleGoAuth()
      } else {
        this.handleGoLogin()
      }
    },
    handleGoLogin() {
      // 触发全局事件，通知需要打开登录弹窗
      window.dispatchEvent(new CustomEvent('show-login-dialog'))
      this.handleClose()
    },
    handleGoAuth() {
      if (!this.$auth.isLoggedIn()) {
        ElMessage.warning('请先登录后再进行身份认证')
        window.dispatchEvent(new CustomEvent('show-login-dialog'))
        return
      }
      // 跳转到身份认证页面（根据主体类型跳转到个人/企业认证）
      const authPath = this.$auth.hasPersonalType()
        ? '/user-center/account/personal-auth'
        : '/user-center/account/enterprise-auth'
      this.$router.push(authPath)
      this.handleClose()
    }
  }
}
</script>

<style lang="scss" scoped>
.login-auth-dialog {
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  margin: 16px auto;
  padding: 0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  :deep(.el-dialog__header) {
    display: none;
  }

  :deep(.el-dialog__body) {
    min-height: 0;
    padding: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }
}

.dialog-content {
  min-width: 0;
  padding: 28px;

  .dialog-text {
    margin-bottom: 30px;
    line-height: 1.8;
    font-weight: 600;

    p {
      margin: 0;
      font-size: 14px;
      color: #333333;
      overflow-wrap: anywhere;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .link-text {
      appearance: none;
      padding: 0;
      border: 0;
      background: transparent;
      color: #0D62F3;
      cursor: pointer;
      font: inherit;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }

      &:focus-visible {
        border-radius: 2px;
        outline: 2px solid #0D62F3;
        outline-offset: 2px;
      }
    }
  }

  .dialog-footer {
    display: flex;
    justify-content: center;
    gap: 16px;

    :deep(.el-button + .el-button) {
      margin-left: 0;
    }

    .cancel-btn {
      width: 120px;
      min-width: 0;
      height: 40px;
      background: #ffffff;
      border: 1px solid #0D62F3;
      color: #0D62F3;
      border-radius: 4px;
      font-size: 14px;

      &:hover {
        background: #ecf5ff;
        border-color: #0D62F3;
        color: #0D62F3;
      }

      &:active {
        background: #ffffff;
        border-color: #0D62F3;
        color: #0D62F3;
      }
    }
    
    .confirm-btn {
      width: 120px;
      min-width: 0;
      height: 40px;
      background: #0D62F3;
      border: none;
      border-radius: 4px;
      font-size: 14px;

      &:hover {
        background: #0D62F3;
      }

      &:active {
        background: #0D62F3;
      }
    }
  }
}

@media (max-width: 768px) {
  .login-auth-dialog {
    width: calc(100vw - 32px) !important;
    max-width: 468px;
  }

  .dialog-content {
    padding: 24px;

    .dialog-text {
      margin-bottom: 24px;
      line-height: 1.7;
    }

    .dialog-footer {
      gap: 12px;

      .cancel-btn,
      .confirm-btn {
        flex: 1;
        width: auto;
        max-width: 160px;
        min-height: 44px;
      }
    }
  }
}

@media (max-width: 480px) {
  .login-auth-dialog {
    width: calc(100vw - 24px) !important;
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    margin: 12px auto;
  }

  .dialog-content {
    padding: 22px 18px calc(22px + env(safe-area-inset-bottom, 0px));

    .dialog-text {
      margin-bottom: 22px;
    }

    .dialog-footer {
      .cancel-btn,
      .confirm-btn {
        max-width: none;
      }
    }
  }
}

@media (max-width: 360px) {
  .dialog-content {
    padding-right: 16px;
    padding-left: 16px;

    .dialog-footer {
      flex-direction: column;
      gap: 10px;

      .cancel-btn,
      .confirm-btn {
        width: 100%;
      }
    }
  }
}

@media (max-height: 480px) {
  .dialog-content {
    padding-top: 18px;
    padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));

    .dialog-text {
      margin-bottom: 18px;
    }
  }
}
</style>

